/**
 * ExistingTicketRenderer — adapter that lets the approval renderer
 * registry dispatch to our bespoke ExistingTicketCard.
 *
 * Mirrors DraftTicketRenderer / SubmittedTicketRenderer: validates the
 * payload shape and falls back to a small inline error if it isn't
 * shaped as expected.
 */

import type { ReactNode } from 'react'
import { ExistingTicketCard } from '../components/ExistingTicketCard'
import type { ExistingTicketPayload } from '../types'

interface RendererProps {
  data: unknown
}

function isExistingPayload(value: unknown): value is ExistingTicketPayload {
  if (!value || typeof value !== 'object') return false
  const obj = value as Record<string, unknown>
  // Title and ticket_id are the only fields we strictly require — the
  // backend may omit optional fields (description, status, etc.) for
  // very-new or incomplete tickets.
  return typeof obj.ticket_id === 'string' && typeof obj.title === 'string'
}

export function ExistingTicketRenderer({ data }: RendererProps): ReactNode {
  if (!isExistingPayload(data)) {
    return (
      <div role="alert" style={errorStyle}>
        Existing ticket data is missing or malformed.
      </div>
    )
  }
  return <ExistingTicketCard data={data} variant="compact" />
}

const errorStyle = {
  padding: '10px 12px',
  fontSize: '12px',
  color: 'var(--error)',
  border: '1px solid rgba(239, 68, 68, 0.3)',
  background: 'rgba(239, 68, 68, 0.08)',
  borderRadius: 'var(--radius-sm)',
}

export default ExistingTicketRenderer
